import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@shared/components/ui/button";
import { Input } from "@shared/components/ui/input";
import {
  useExhibitList,
  useDeleteExhibit,
  useGenerateExhibitQR,
  useTransitionExhibit,
} from "@shared/api/hooks/useExhibits";
import { ApiError } from "@shared/api/client";
import type { ExhibitListItem } from "@shared/api/exhibits";
import type { ListStatus } from "@shared/api/listStatus";
import { Plus, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

import ExhibitListRow from "./ExhibitListRow";
import StatusFilter from "@staff/components/StatusFilter";

const StaffDashboard = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [status, setStatus] = useState<ListStatus>("active");

  // Publish toggle + QR generate use React Query hooks; toggle reads
  // `state` and dispatches via useTransitionExhibit.
  const { data: exhibits = [], isLoading: loading } = useExhibitList(status);

  const deleteExhibitMutation = useDeleteExhibit();
  const transitionMutation = useTransitionExhibit();
  const generateQRMutation = useGenerateExhibitQR();

  const handleTogglePublish = (
    exhibitId: string,
    currentState: ExhibitListItem["state"],
  ) => {
    const nextState = currentState === "published" ? "draft" : "published";
    transitionMutation.mutate(
      { exhibitId, payload: { to_state: nextState } },
      {
        onSuccess: () => {
          toast.success(
            currentState === "published"
              ? "Exhibit unpublished"
              : "Exhibit published",
          );
        },
        onError: (err) => {
          // Surface the backend error envelope so publish-gate failures
          // (e.g. missing_cover_image per ADR-0005) aren't masked.
          const message =
            err instanceof ApiError && err.message
              ? err.message
              : "Failed to update publish status";
          toast.error(message);
        },
      },
    );
  };

  const handleDelete = (exhibitId: string) => {
    deleteExhibitMutation.mutate(exhibitId, {
      onSuccess: () => toast.success("Exhibit deleted"),
      onError: () => toast.error("Failed to delete exhibit"),
    });
  };

  const handleGenerateQR = (exhibitId: string) => {
    generateQRMutation.mutate(exhibitId, {
      onSuccess: () => toast.success("QR code generated"),
      onError: () => toast.error("Failed to generate QR code"),
    });
  };

  const filteredExhibits = exhibits.filter(
    (exhibit) =>
      exhibit.titleTc.toLowerCase().includes(searchQuery.toLowerCase()) ||
      exhibit.titleEn.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  return (
    <div
      className="min-h-screen bg-background"
      data-testid="staff-dashboard-page"
    >
      {/* Header — subpage of the Exhibitions home; the top menu bar
          lives there, so this page only carries a back arrow. */}
      <div className="bg-white border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-3">
          <Button
            onClick={() => navigate("/staff/exhibitions")}
            variant="ghost"
            size="sm"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="text-2xl font-heading font-bold">
            Exhibit Management
          </h1>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Actions Bar */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <Input
            placeholder="Search exhibits..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="max-w-xs"
            data-testid="search-input"
          />
          <StatusFilter value={status} onChange={setStatus} />
          <Button
            onClick={() => navigate("/staff/exhibit/create")}
            data-testid="create-exhibit-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create New Exhibit
          </Button>
        </div>

        {/* Exhibits Table */}
        {loading ? (
          <div className="text-center py-12" data-testid="loading-state">
            Loading...
          </div>
        ) : filteredExhibits.length === 0 ? (
          <div
            className="text-center py-12 border border-border rounded-lg"
            data-testid="empty-state"
          >
            <p className="text-muted-foreground">No exhibits found</p>
          </div>
        ) : (
          <div className="bg-white border border-border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="exhibits-table">
                <thead className="bg-muted/50 border-b border-border">
                  <tr>
                    <th className="text-left px-6 py-3 text-sm font-medium">
                      Code
                    </th>
                    <th className="text-left px-6 py-3 text-sm font-medium">
                      Image
                    </th>
                    <th className="text-left px-6 py-3 text-sm font-medium">
                      Title (TC)
                    </th>
                    <th className="text-left px-6 py-3 text-sm font-medium">
                      Status
                    </th>
                    <th className="text-left px-6 py-3 text-sm font-medium">
                      QR Code
                    </th>
                    <th className="text-left px-6 py-3 text-sm font-medium">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredExhibits.map((exhibit) => (
                    <ExhibitListRow
                      key={exhibit.id}
                      exhibit={exhibit}
                      onTogglePublish={handleTogglePublish}
                      onGenerateQR={handleGenerateQR}
                      onEdit={(id) => navigate(`/staff/exhibit/${id}/edit`)}
                      onDelete={handleDelete}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StaffDashboard;
